import { useState, useCallback, useMemo } from "react";
import { SHIP_PARTS, RARITY_ORDER, computeShipPartBonuses } from "../data/shipParts";

// ── useShipParts ──────────────────────────────────────────────────────────────
// Manages which tiers the player has unlocked (found) and which are equipped.
//
// savedOwnedTiers:    { [partId]: highestTierIndexFound }  (default all 0 = common)
// savedEquippedTiers: { [partId]: equippedTierIndex }      (default all 0)
//
export function useShipParts({ savedOwnedTiers, savedEquippedTiers }) {
  // ownedTiers: highest tier index the player has ever found for each part
  const [ownedTiers, setOwnedTiers] = useState(() => {
    const base = {};
    for (const p of SHIP_PARTS) base[p.id] = 0; // common always owned
    return { ...base, ...(savedOwnedTiers ?? {}) };
  });

  // equippedTiers: which tier index is currently slotted for each part
  const [equippedTiers, setEquippedTiers] = useState(() => {
    const base = {};
    for (const p of SHIP_PARTS) base[p.id] = 0;
    return { ...base, ...(savedEquippedTiers ?? {}) };
  });

  // Grant a found part (partId + tierIdx).  If this tier is higher than what
  // the player already owns for this part, it upgrades ownedTiers.
  // Returns true if it was genuinely new (higher tier than current owned).
  const grantShipPart = useCallback((partId, tierIdx) => {
    let isNew = false;
    setOwnedTiers(prev => {
      const current = prev[partId] ?? 0;
      if (tierIdx <= current) return prev; // already have this tier or better
      isNew = true;
      return { ...prev, [partId]: tierIdx };
    });
    return isNew;
  }, []);

  // Equip a specific tier for a part (only allowed if owned)
  const equipTier = useCallback((partId, tierIdx) => {
    setEquippedTiers(prev => {
      const owned = ownedTiers[partId] ?? 0;
      if (tierIdx > owned) return prev; // can't equip what you don't own
      return { ...prev, [partId]: tierIdx };
    });
  }, [ownedTiers]);

  // Stat bonuses contributed by currently equipped ship parts
  const shipPartBonuses = useMemo(() => {
    return computeShipPartBonuses(equippedTiers);
  }, [equippedTiers]);

  // Rarity label for the best owned tier of a given part
  function bestOwnedRarity(partId) {
    return RARITY_ORDER[ownedTiers[partId] ?? 0] ?? "common";
  }

  return {
    ownedTiers,
    equippedTiers,
    shipPartBonuses,
    grantShipPart,
    equipTier,
    bestOwnedRarity,
    setOwnedTiers,
    setEquippedTiers,
  };
}
